// Survei Kepuasan Peserta Didik -- fungsi murni halaman publik /survei & halaman admin (diuji survei.test.js).
// Butir, skala, & batas teks SELALU dari server (GET /survei/publik/{kode}/butir); di sini hanya penyusun tampilan.
import { formatTanggalPendek } from "./datetime";

// Tautan survei per periode: /survei-peserta-didik-<kode>. Kode 8 karakter dari alfabet tanpa 0/o/1/l/i -- cermin
// survei.ALFABET_KODE di backend. Bukan pengaman (tautan bisa diteruskan) & tidak per orang.
export const ALFABET_KODE_SURVEI = "abcdefghjkmnpqrstuvwxyz23456789";
export const AWALAN_TAUTAN_SURVEI = "/survei-peserta-didik-";
const POLA_ALAMAT_SURVEI = new RegExp(`^${AWALAN_TAUTAN_SURVEI}([${ALFABET_KODE_SURVEI}]{8})/?$`, "i");

// Kode dari pathname ("/survei-peserta-didik-abcd2345" -> "abcd2345"); huruf besar diterima & dijadikan kecil (tautan
// yg diketik ulang / diubah aplikasi pesan); selain itu null.
export function kodeSurveiDariAlamat(pathname) {
    const cocok = POLA_ALAMAT_SURVEI.exec(pathname || "");
    return cocok ? cocok[1].toLowerCase() : null;
}

// Kelompok survei (PR #112): Kedokteran (bawaan) & Kepnakes (Keperawatan & Nakes, institusi pendidikan luar). Awalan
// alamat per kelompok = cermin survei_kepnakes.AWALAN_TAUTAN di backend. Pencocokan KETAT per awalan; kode dgn awalan
// kelompok yg salah ditolak server (404 sama dgn kode tak dikenal) -> halaman "Tautan survei tidak valid".
export const KELOMPOK_KEDOKTERAN = "kedokteran";
export const KELOMPOK_KEPNAKES = "kepnakes";
export const LABEL_KELOMPOK = { [KELOMPOK_KEDOKTERAN]: "Kedokteran", [KELOMPOK_KEPNAKES]: "Kepnakes" };
export const AWALAN_TAUTAN_KEPNAKES = "/survei-keperawatan-nakes-";
const POLA_ALAMAT_KEPNAKES = new RegExp(`^${AWALAN_TAUTAN_KEPNAKES}([${ALFABET_KODE_SURVEI}]{8})/?$`, "i");

// {kode, kelompok} dari pathname, atau null bila bukan alamat survei.
export function alamatSurvei(pathname) {
    const kode = kodeSurveiDariAlamat(pathname);
    if (kode) return { kode, kelompok: KELOMPOK_KEDOKTERAN };
    const cocok = POLA_ALAMAT_KEPNAKES.exec(pathname || "");
    return cocok ? { kode: cocok[1].toLowerCase(), kelompok: KELOMPOK_KEPNAKES } : null;
}

export const tautanSurvei = (kode, origin, kelompok = KELOMPOK_KEDOKTERAN) =>
    `${origin}${kelompok === KELOMPOK_KEPNAKES ? AWALAN_TAUTAN_KEPNAKES : AWALAN_TAUTAN_SURVEI}${kode}`;

// Tautan lengkap satu periode di halaman admin: path dari server (tautan_path), cadangan dari kode + kelompok.
export const tautanPeriode = (p, origin) =>
    p.tautan_path ? `${origin}${p.tautan_path}` : (p.kode ? tautanSurvei(p.kode, origin, p.kelompok) : null);

// Parameter query API publik: Kepnakes mengirim ?kelompok=kepnakes; Kedokteran tanpa parameter (persis seperti dulu).
export const paramKelompok = (kelompok) => (kelompok === KELOMPOK_KEPNAKES ? { params: { kelompok } } : undefined);

export const TEKS_TAUTAN_TIDAK_VALID = "Tautan survei tidak valid. Gunakan tautan terbaru dari UFP.";
export const TEKS_SURVEI_BERAKHIR = "Survei ini sudah berakhir. Terima kasih.";
export const TEKS_TAUTAN_BERUBAH = "Tautan survei telah berubah. Silakan gunakan tautan terbaru dari Unit Fungsional Pendidikan (WA 0851-7989-5686).";
export const TEKS_KETERANGAN_TAUTAN = "Tautan ini unik untuk periode ini. Periode baru akan memiliki tautan baru.";

// jawaban = objek { [no butir]: angka }; hanya disimpan di memori halaman (keputusan pemilik).
export const jumlahTerjawab = (jawaban) => Object.keys(jawaban).length;

export const totalButir = (dimensi) => dimensi.reduce((n, d) => n + d.butir.length, 0);

export const persenProgres = (jawaban, dimensi) => {
    const total = totalButir(dimensi);
    return total ? Math.round((jumlahTerjawab(jawaban) / total) * 100) : 0;
};

// Nomor butir pertama yg belum dijawab di satu dimensi (null = lengkap).
export const butirBelumDijawab = (dimensiSatu, jawaban) =>
    dimensiSatu.butir.find((b) => jawaban[b.no] === undefined)?.no ?? null;

export const semuaTerjawab = (dimensi, jawaban) => dimensi.every((d) => butirBelumDijawab(d, jawaban) === null);

// Daftar angka urut nomor butir 1..N -- bentuk yg diterima POST /survei/publik/kirim.
export const susunJawaban = (dimensi, jawaban) => {
    const nomor = dimensi.flatMap((d) => d.butir.map((b) => b.no)).sort((a, b) => a - b);
    return nomor.map((n) => jawaban[n]);
};

// Opsi TB (PR #109): jawaban "TB" = Tidak Berlaku; dihitung terisi di bilah kemajuan; minimal satu jawaban angka.
export const TOKEN_TB = "TB";
export const PESAN_MINIMAL_ANGKA = "Minimal satu pernyataan harus dinilai dengan angka.";
export const adaJawabanAngka = (jawaban) => Object.values(jawaban).some((v) => typeof v === "number");
// Opsi TB utk satu butir (null = tidak boleh TB). Respons /butir lama tanpa opsi_tb = tanpa TB.
export const tbButir = (butir, satu) => (butir?.opsi_tb?.aktif && satu.boleh_tb !== false ? butir.opsi_tb : null);
export const teksLegendaTb = (opsi) => `Pilih ${opsi.label} bila pernyataan tidak berlaku bagi Anda.`;

export const nilaiSkala = (skala) =>
    Array.from({ length: skala.maks - skala.min + 1 }, (_, i) => skala.min + i);

export const labelProdi = (jenjang) => (jenjang === "Koas" ? "Modul" : "Program Studi");

export const panjangTeks = (teks) => (teks || "").length;

// Teks pendahuluan /survei -- ditulis PERSIS seperti yang diberikan pemilik (memuat pernyataan anonimitas &
// kontak resmi): jangan diparafrase atau ditambah klaim. `**kata**` = tebal (diurai pecahTebal, dirender sbg
// elemen <strong>, bukan HTML mentah). `inti` = tampil juga saat bagian lain terlipat di HP (P1 sapaan, P4
// anonimitas, P5 cara pengisian + peringatan "jawaban sementara tidak tersimpan" yg TIDAK boleh terlipat).
export const PENDAHULUAN = [
    { id: "p1", inti: true, teks: "Selamat pagi/siang/sore/malam, peserta didik yang terhormat," },
    { id: "p2", inti: false, teks: "**SURVEI OPINI PESERTA DIDIK** ini merupakan kegiatan yang secara khusus diadakan untuk memperoleh informasi tentang pendapat peserta didik terkait pengalaman menjalani pendidikan klinik di RS Dr. Cipto Mangunkusumo (RSCM). Seluruh peserta didik wajib berpartisipasi dalam survei ini." },
    { id: "p3", inti: false, teks: "Kami berterima kasih Anda telah bersedia menjadi narasumber dalam kegiatan ini. Kami sangat mengharapkan kesediaan Anda untuk memberikan pendapat sesuai pengalaman pribadi Anda." },
    { id: "p4", inti: true, teks: "Untuk memastikan hasil pengisian kuesioner ini bermanfaat bagi peningkatan mutu pendidikan yang diberikan RSCM, Anda diharapkan mengisi secara spontan tanpa bertukar pikiran dengan orang lain. Anda tidak perlu menuliskan nama. Di awal, Anda diminta memasukkan NPM dan tanggal lahir hanya untuk memastikan Anda peserta didik aktif dan mencegah pengisian ganda. Jawaban Anda disimpan terpisah, tanpa nama, NPM, maupun tanggal lahir, dan hasil survei dilaporkan secara kelompok, bukan per individu. Data Anda diolah hanya untuk kepentingan survei ini, sehingga tidak perlu takut untuk memberikan pendapat dengan sejujur-jujurnya." },
    { id: "p5", inti: true, teks: "Survei ini terdiri dari {jumlah_pernyataan} pernyataan dalam {jumlah_bagian} bagian (skala {skala_min} = sangat tidak setuju sampai {skala_maks} = sangat setuju) dan {jumlah_terbuka} pertanyaan terbuka, dengan waktu pengisian sekitar 20–30 menit. Survei hanya dapat diisi satu kali. Jawaban sementara tidak tersimpan, jadi mohon diselesaikan dalam satu kali pengisian dan jangan menutup atau memuat ulang halaman sebelum mengirim." },
    { id: "p6", inti: false, teks: "Perlu diingat bahwa dalam pengisian kuesioner ini **tidak ada jawaban benar ataupun salah**. Jawaban yang paling tepat adalah yang menggambarkan apa yang Anda rasakan secara pribadi sebagai peserta didik di RSCM. Masukan Anda akan sangat membantu RSCM, FKUI, dan Program Studi untuk dapat melayani Anda dengan lebih baik selama menjalani pendidikan." },
    { id: "p7", inti: false, teks: "Terima kasih. Selamat mengisi.\n**RSUPN Dr. Cipto Mangunkusumo**\nJl. Diponegoro 71, Jakarta 10430\n**Unit Fungsional Pendidikan**\nNo. HP: 0851-7989-5686" },
];

// Pendahuluan Kepnakes (PR #112): SAMA dgn PENDAHULUAN kecuali kalimat yg khusus NPM/tanggal lahir (P4) dan FKUI (P6) --
// Kepnakes tidak diverifikasi & berasal dari institusi pendidikan luar. Perubahan (menunggu persetujuan pemilik):
// P4 dihapus "Di awal, Anda diminta memasukkan NPM dan tanggal lahir ... mencegah pengisian ganda." & "Jawaban Anda disimpan
// terpisah, tanpa nama, NPM, maupun tanggal lahir, dan hasil" -> "Jawaban Anda disimpan tanpa nama dan hasil";
// P6 "RSCM, FKUI, dan Program Studi" -> "RSCM, institusi pendidikan Anda, dan Program Studi".
// PR #113 (keputusan pemilik): P5 "Survei hanya dapat diisi satu kali." -> "Mohon mengisi satu kali saja." (Kepnakes tidak
// memblokir pengisian ulang).
const UBAH_KEPNAKES = {
    p5: [["Survei hanya dapat diisi satu kali.", "Mohon mengisi satu kali saja."]],
    p4: [
        [" Di awal, Anda diminta memasukkan NPM dan tanggal lahir hanya untuk memastikan Anda peserta didik aktif dan mencegah pengisian ganda.", ""],
        ["Jawaban Anda disimpan terpisah, tanpa nama, NPM, maupun tanggal lahir, dan hasil", "Jawaban Anda disimpan tanpa nama dan hasil"],
    ],
    p6: [["RSCM, FKUI, dan Program Studi", "RSCM, institusi pendidikan Anda, dan Program Studi"]],
};
export const PENDAHULUAN_KEPNAKES = PENDAHULUAN.map((p) => ({
    ...p, teks: (UBAH_KEPNAKES[p.id] || []).reduce((t, [lama, baru]) => t.replace(lama, baru), p.teks),
}));

// Nama bagian di formulir PUBLIK Kepnakes (PR #113): bagian yg namanya memuat "Residency"/"Recidency" (istilah residensi
// kedokteran) ditampilkan "Kepuasan keseluruhan". Hanya tampilan -- data versi butir & Excel TIDAK berubah.
export const NAMA_BAGIAN_KEPUASAN_KESELURUHAN = "Kepuasan keseluruhan";
export const namaBagianKepnakes = (nama) => (/re[sc]idency/i.test(nama || "") ? NAMA_BAGIAN_KEPUASAN_KESELURUHAN : nama);

// Langkah demografi Kepnakes: label rentang usia (nilai server tetap "<=30" dst.).
export const LABEL_RENTANG_USIA = { "<=30": "30 tahun atau kurang", "31-40": "31–40 tahun", "41-50": "41–50 tahun", ">=51": "51 tahun atau lebih" };
export const labelProdiKepnakes = (p) => `${p.nama} (${p.jenjang})`;
// Program Studi milik institusi terpilih (pilihan.institusi[].prodi, bersarang dari server); [] bila institusi belum dipilih.
export const prodiDariInstitusi = (pilihan, institusiId) =>
    (pilihan?.institusi || []).find((i) => i.id === institusiId)?.prodi || [];
export const TEKS_PILIH_INSTITUSI_DULU = "Pilih institusi terlebih dahulu.";
export const TEKS_TANPA_PILIHAN_PRODI = "Tidak ada pilihan program studi.";
// Isian wajib demografi yg belum dipilih (urut tampil); jenis kelamin hanya bila ditanyakan periode ini. Program Studi
// hanya wajib bila institusi terpilih punya Program Studi aktif (kalau tidak, boleh kosong).
export function demografiBelumLengkap(isian, pilihan) {
    const belum = [];
    if (!isian.institusi_id) belum.push("Institusi");
    if (!isian.prodi_id && (!isian.institusi_id || prodiDariInstitusi(pilihan, isian.institusi_id).length > 0)) belum.push("Program Studi");
    if (!isian.rentang_usia) belum.push("Rentang Usia");
    if (pilihan?.jenis_kelamin && !isian.jenis_kelamin) belum.push("Jenis Kelamin");
    return belum;
}
export const isiDemografiKirim = (isian, pilihan) => ({
    institusi_id: isian.institusi_id, ...(isian.prodi_id ? { prodi_id: isian.prodi_id } : {}), rentang_usia: isian.rentang_usia,
    ...(pilihan?.jenis_kelamin ? { jenis_kelamin: isian.jenis_kelamin } : {}),
});

// Pemberitahuan lembut "sudah dikirim dari perangkat ini" (Kepnakes; TIDAK memblokir). Penanda ringan per kode periode di
// localStorage, dibungkus try/catch: penyimpanan tidak tersedia = halaman tetap jalan tanpa pemberitahuan.
export const TEKS_SUDAH_DIKIRIM_PERANGKAT = "Survei sudah dikirim dari perangkat ini. Jika Anda orang lain yang mengisi, silakan lanjut.";
const kunciPenanda = (kode) => `aura_survei_kepnakes_terkirim_${kode}`;
export function sudahDikirimDariPerangkat(kode) {
    try { return window.localStorage.getItem(kunciPenanda(kode)) === "1"; } catch { return false; }
}
export function tandaiDikirimDariPerangkat(kode) {
    try { window.localStorage.setItem(kunciPenanda(kode), "1"); } catch { /* penyimpanan tidak tersedia */ }
}

// Angka pendahuluan dari versi butir periode (respons GET /survei/publik/{kode}/butir) -- tidak ada angka tertulis mati.
export const angkaPendahuluan = (butir) => ({
    jumlah_pernyataan: totalButir(butir.dimensi), jumlah_bagian: butir.dimensi.length,
    skala_min: butir.skala.min, skala_maks: butir.skala.maks, jumlah_terbuka: butir.terbuka.length,
});

// "{jumlah_pernyataan}" dst. -> angka. Penanda tanpa angka dibiarkan apa adanya (kelihatan saat uji, tidak diam-diam hilang).
export const isiAngka = (teks, angka) =>
    String(teks).replace(/\{([a-z_]+)\}/g, (cocok, kunci) => (angka && angka[kunci] !== undefined ? String(angka[kunci]) : cocok));

// "a **b** c" -> [{teks:"a ",tebal:false},{teks:"b",tebal:true},{teks:" c",tebal:false}]. Bila penanda ** tidak
// berpasangan, SELURUH teks dikembalikan apa adanya sbg teks biasa (tidak ada tanda yg hilang diam-diam).
export function pecahTebal(teks) {
    const bagian = String(teks).split("**");
    if (bagian.length % 2 === 0) return [{ teks: String(teks), tebal: false }];
    return bagian.map((t, i) => ({ teks: t, tebal: i % 2 === 1 })).filter((b) => b.teks !== "");
}

// Pesan layar bila periode tidak sedang dibuka.
export function pesanKeadaan(keadaan, periode) {
    if (keadaan === "belum" && periode) {
        return `Survei "${periode.nama}" baru dibuka pada ${formatTanggalPendek(periode.tanggal_mulai)}.`;
    }
    if (keadaan === "berakhir") return TEKS_SURVEI_BERAKHIR;
    return "Survei sedang tidak dibuka.";
}

// Label keadaan periode di halaman admin.
export const LABEL_KEADAAN = {
    buka: "Dibuka", belum: "Dijadwalkan", tutup: "Lewat tanggal", tidak_ada: "Ditutup",
};

export const formatPersen = (v) =>
    v === null || v === undefined ? "–" : `${v.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// Periode bawaan di Cakupan/Hasil: yg aktif, kalau tidak ada yg pertama (terbaru dari server).
export const periodeBawaan = (daftar) => (daftar.find((p) => p.aktif) || daftar[0] || null)?.id ?? "";

// Unduh Excel hasil survei (komponen SurveiUnduhExcel).
export const TEKS_BANTU_UNDUH_SURVEI = "Berkas rahasia. Kelompok kurang dari 5 responden disembunyikan. Pengunduhan dicatat di log audit.";
// Periode masih dibuka -> unduhan hanya berisi lembar Info + Cakupan jenjang (teks SAMA dgn lembar Info di server).
export const TEKS_PERIODE_DIBUKA_UNDUH = "Periode masih dibuka. Hasil lengkap dapat diunduh setelah periode ditutup (mencegah pembandingan unduhan sementara yang bisa membuka jawaban individu).";
export const TEKS_KONFIRMASI_ISIAN_TERBUKA = "Isian terbuka bisa memuat identitas bila responden menuliskannya. Simpan berkas hanya di perangkat Anda, jangan dibagikan.";

// Pesan galat unduh tanpa detail server (respons blob tidak dibaca; detail teknis tidak ditampilkan).
export function pesanGagalUnduhSurvei(status) {
    if (status === 403) return "Anda tidak berhak mengunduh hasil survei.";
    if (status === 404) return "Periode survei tidak ditemukan. Muat ulang halaman.";
    return "Gagal menyiapkan berkas. Coba lagi beberapa saat.";
}

// Label opsi tanpa jawaban lurus: jumlah butir dari versi periode (belum diketahui -> tanpa angka).
export const labelTanpaLurus = (jumlahButir) =>
    `Tanpa jawaban lurus (${jumlahButir ? `ke-${jumlahButir}` : "semua"} jawaban sama)`;

// Tab Hasil: periode masih dibuka -> layar tanpa angka (server juga tidak mengirim angka).
export const TEKS_HASIL_PERIODE_DIBUKA = "Hasil tersedia setelah periode ditutup. (Mencegah pembandingan angka sementara yang bisa membuka jawaban individu.)";
// Admin UFP tidak bisa menutup periode: keterangan tambahan saat periode masih dibuka.
export const TEKS_HASIL_TUNGGU_SUPER_ADMIN = "Hasil tampil setelah periode ditutup oleh Super Admin.";
export const TEKS_FITUR_NONAKTIF_BACA = "Survei belum aktif di server. Silakan hubungi Super Admin.";
// Tab halaman admin: Kelola (Super Admin) & Baca (Admin UFP; Gabungan ditampilkan di dalam tab Hasil).
export const LABEL_TAB_SURVEI = { periode: "Periode", tautan: "Tautan", cakupan: "Cakupan", hasil: "Hasil", gabungan: "Gabungan", butir: "Butir", master: "Master Kepnakes" };
export const TAB_SURVEI_KELOLA = ["periode", "tautan", "cakupan", "hasil", "gabungan", "butir", "master"];
export const TAB_SURVEI_BACA = ["cakupan", "hasil", "tautan", "butir", "master"];
export const LINGKUP_SEMUA = "semua";
export const LINGKUP_PRODI = "prodi";
export const LINGKUP_INSTITUSI = "institusi"; // Kepnakes (PR #113)

// Nilai Rekap (rata-rata individu x 10, skala 0-100): dua desimal gaya Indonesia, kosong -> "–".
export const formatNilaiRekap = (v) =>
    v === null || v === undefined ? "–" : v.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Parameter GET /survei/periode/{id}/hasil. Prodi hanya dikirim pada lingkup "Pilih prodi" yg sudah dipilih.
export function paramHasil(lingkup, prodiId, tanpaLurus) {
    if (lingkup === LINGKUP_INSTITUSI) return prodiId ? { tanpa_lurus: tanpaLurus, institusi_id: prodiId } : { tanpa_lurus: tanpaLurus };
    return lingkup === LINGKUP_PRODI && prodiId ? { tanpa_lurus: tanpaLurus, prodi_id: prodiId } : { tanpa_lurus: tanpaLurus };
}

// Parameter GET .../ekspor per tombol. "zip" = satu berkas per prodi yg memenuhi syarat. Isian terbuka hanya utk "semua".
export function paramEkspor(jenis, { prodiId, tanpaLurus, sertakanTerbuka }) {
    const tl = tanpaLurus ? 1 : 0;
    if (jenis === "zip") return { lingkup: "zip", tanpa_lurus: tl };
    if (jenis === LINGKUP_PRODI) return { lingkup: "prodi", prodi_id: prodiId, tanpa_lurus: tl };
    if (jenis === LINGKUP_INSTITUSI) return { lingkup: "institusi", institusi_id: prodiId, tanpa_lurus: tl };
    return { lingkup: "semua", tanpa_lurus: tl, sertakan_terbuka: sertakanTerbuka ? 1 : 0 };
}

// Teks kelompok (PR #113): Kedokteran per program studi, Kepnakes per institusi. Satu sumber utk tab Hasil & Unduh Excel.
export const TEKS_LINGKUP = {
    kedokteran: { semua: "Semua prodi", pilih: "Pilih prodi", placeholder: "Pilih program studi", zip: "Unduh semua prodi (ZIP)",
                  lingkupPilih: LINGKUP_PRODI, pilihDulu: "Pilih program studi yang sudah memenuhi batas minimal 5 responden." },
    kepnakes: { semua: "Semua institusi", pilih: "Pilih institusi", placeholder: "Pilih institusi", zip: "Unduh per institusi (ZIP)",
                lingkupPilih: LINGKUP_INSTITUSI, pilihDulu: "Pilih institusi yang sudah memenuhi batas minimal 5 responden." },
};
export const teksLingkup = (kelompok) => TEKS_LINGKUP[kelompok === KELOMPOK_KEPNAKES ? KELOMPOK_KEPNAKES : KELOMPOK_KEDOKTERAN];
export const TEKS_KELOMPOK_KECIL_INSTITUSI = "Institusi dengan kurang dari 5 responden (atau yang terpaksa digabung agar angkanya tidak dapat dihitung mundur) masuk kolom \"Gabungan (kelompok kecil)\" dan tidak bisa dipilih. Kolom SELURUH KEPNAKES tetap dihitung dari semua responden.";
// Pilihan institusi dari server (institusi_pilihan) -> bentuk pilihan prodi agar komponen yang sama bisa dipakai.
export const pilihanDariInstitusi = (daftar) => (daftar || []).map((i) => ({ prodi_id: i.institusi_id, nama: i.nama, jenjang: null, boleh: i.boleh, alasan: i.alasan }));

// Gabungan (Kedokteran + Kepnakes): periode yg boleh dipilih = sudah DITUTUP; periode Kepnakes harus berversi butir SAMA.
export const TEKS_SYARAT_GABUNGAN = "Gabungan membutuhkan satu periode Kedokteran dan satu periode Kepnakes yang sudah ditutup dan memakai versi butir yang sama.";
export const periodeGabungan = (daftar, kelompok) => (daftar || []).filter((p) => !p.aktif && (p.kelompok || KELOMPOK_KEDOKTERAN) === kelompok);
export const kepnakesCocok = (daftar, kedokteran) =>
    periodeGabungan(daftar, KELOMPOK_KEPNAKES).filter((p) => kedokteran && p.versi_butir?.id && p.versi_butir.id === kedokteran.versi_butir?.id);

// Label pilihan prodi: "Nama (Jenjang)" + alasan bila belum bisa dipilih (tanpa jumlah responden).
export const labelPilihanProdi = (p) =>
    `${p.nama || p.prodi_id}${p.jenjang ? ` (${p.jenjang})` : ""}${p.boleh ? "" : ` — ${p.alasan}`}`;
